import axios, { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiBackgroundLoadingErrorsAtom,
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  apiPost,
  ApiSuffixType,
  companyIdSelector,
  createErrorResult,
  createErrorResultFromException,
  createErrorResultFromTranslatableItem,
  CrudApi,
  DrawerType,
  errors,
  getJourneyBulkCloneRidesPath,
  IApiError,
  IBulkCloneJourneyPayload,
  IFetchOptions,
  IRideDto,
  IRideTableDto,
  IRydeProcess,
  ISearchCriteria,
  Log,
  normalizeErrors,
  RideEntity,
  ridesJourneyAF,
  RydeProcessTitle,
  SimpleResult,
  SortEnumAPI,
  TError,
  toForbiddenTranslatableItem,
  translateInfoItem,
  useDrawers,
  warnAndToastException,
} from "@ryde/shared";

export const useRidesJourneyActions = () => {
  const { t } = useTranslation();
  const companyId = useRecoilValue(companyIdSelector);
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setBackgroundLoadingErrors = useSetRecoilState(
    apiBackgroundLoadingErrorsAtom,
  );
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const setRidesJourneyAtomData = useEvent(
    useRecoilCallback(({ set }) => (rideId: number, value: IRideDto[]) => {
      set(ridesJourneyAF(rideId), value);
    }),
  );

  const { openDrawer } = useDrawers();

  const fetchRidesJourney = useEvent(
    async (
      journeyId: number,
      ridesIds: number[],
      fetchOptions?: IFetchOptions,
    ): Promise<boolean> => {
      const { abortController, backgroundAction } = fetchOptions ?? {};

      try {
        if (backgroundAction) {
          setBackgroundLoadingErrors(undefined);
        } else {
          setErrors(undefined);
          setLoading(true);
        }

        Log.debug(`Fetching rides dtos for journey ${journeyId}`);

        const body: ISearchCriteria<RideEntity> = {
          where: { id: { $in: ridesIds } },
          sort: SortEnumAPI.SortById,
        };

        const apiResult = await CrudApi.post<SimpleResult<IRideTableDto[]>>({
          entity: "ride",
          data: body,
          suffixType: ApiSuffixType.ServerSearch,
          abortController,
        });

        if (!apiResult?.success) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        Log.debug(`rides dto received for journey ${journeyId}`, apiResult);
        const ridesData = apiResult?.data || [];
        setRidesJourneyAtomData(journeyId, ridesData);

        return true;
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return false;
        }
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          Log.errorAndToast(t(errors.ride.failedFetchingRides), errorDetails);
          return false;
        }

        warnAndToastException({
          error,
          fallbackTranslationKey: errors.ride.failedFetchingRides,
          translationFunction: t,
          logWarnMessage: "Failed to fetch rides for journey",
        });

        if (backgroundAction) {
          setErrors(normalizeErrors(error as IApiError, t));
        } else {
          setBackgroundLoadingErrors(normalizeErrors(error as IApiError, t));
        }

        return false;
      } finally {
        if (!backgroundAction) {
          setLoading(false);
        }
      }
    },
  );

  const fetchRidesForEditingJourney = useEvent(
    (journeyId: number | undefined, rideIds: number[] | undefined) => {
      if (!journeyId || !Array.isArray(rideIds)) {
        return;
      }

      fetchRidesJourney(journeyId, rideIds, { backgroundAction: true });
    },
  );

  const bulkCloneJourneys = useEvent(
    async (
      payload: IBulkCloneJourneyPayload,
    ): Promise<SimpleResult<unknown>> => {
      if (companyId === undefined) {
        Log.error(t("errors.invalidCompanyId"));
        return createErrorResult(
          errors.common.invalidCompanyId,
          "Invalid company ID",
        );
      }

      try {
        setErrors(undefined);
        setLoading(true);
        const requestUrl = getJourneyBulkCloneRidesPath(companyId);
        const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
          body: payload,
        });

        if (result?.success) {
          const resultData = result?.data as IRydeProcess;
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: resultData.id,
              processTitle: RydeProcessTitle.BulkJourneyClone,
              entity: "journey",
            },
          });
          return result;
        }

        return (
          result ||
          createErrorResult(
            errors.common.invalidAPIResponse,
            "Invalid API response",
          )
        );
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToCloneJourney"), errorDetails);
          setErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }
        warnAndToastException({
          error,
          fallbackTranslationKey: "errors.failedToCloneJourney",
          translationFunction: t,
          logWarnMessage: "Failed to clone journey",
        });
        setErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(
          error as TError,
          "Failed to clone journey",
        );
      } finally {
        setLoading(false);
      }
    },
  );

  return { fetchRidesJourney, fetchRidesForEditingJourney, bulkCloneJourneys };
};
